import { Phone, ExternalLink, ShieldCheck, Clock3, HeartPulse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BOOKING_URL } from "@/lib/api";

export default function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 md:pt-20 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 fade-in-up">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-brand-teal mb-6">
            <span className="h-px w-8 bg-brand-gold" />
            Trusted Healthcare Partner · NJ · PA · NY
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 tracking-tight leading-[1.05]">
            Healthcare Support Services{" "}
            <span className="relative inline-block">
              <span className="relative z-10">You Can Rely On</span>
              <span className="absolute left-0 right-0 bottom-1 h-2 bg-amber-200/80 -z-0" aria-hidden="true" />
            </span>
            .
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
            T&amp;J Clinical Solutions is a healthcare support company built on
            reliability, professionalism, and patient-centered care — quietly
            strengthening the operational backbone of providers, facilities,
            and the communities they serve.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              asChild
              data-testid="hero-book-service-button"
              size="lg"
              className="bg-brand-teal hover:bg-brand-teal-hover text-white rounded-sm h-12 px-6 text-sm font-semibold tracking-wide"
            >
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Book a Service <ExternalLink className="h-4 w-4 ml-2" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              data-testid="hero-call-now-button"
              className="rounded-sm h-12 px-6 text-sm font-semibold tracking-wide border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white"
            >
              <a href="tel:5169198679">
                <Phone className="h-4 w-4 mr-2" /> Call · 516-919-8679
              </a>
            </Button>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-200 border border-slate-200" data-testid="hero-trust-strip">
            {[
              { icon: ShieldCheck, title: "HIPAA-Conscious", copy: "Confidential handling" },
              { icon: Clock3, title: "Flexible Scheduling", copy: "Mobile support when you need it" },
              { icon: HeartPulse, title: "Patient-Centered", copy: "Compassion first" },
            ].map(({ icon: Icon, title, copy }) => (
              <div key={title} className="bg-white p-5 flex items-start gap-3">
                <Icon className="h-5 w-5 text-brand-teal shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-slate-900">{title}</div>
                  <div className="text-xs text-slate-500">{copy}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="relative border border-slate-200 bg-slate-50" data-testid="hero-image-frame">
            <img
              src="/tj-team.png"
              alt="The T&J Clinical Solutions team — nurses and physicians in branded scrubs"
              className="w-full h-[420px] md:h-[560px] object-cover"
              loading="eager"
            />
            <div className="absolute -bottom-px left-0 h-1 w-24 bg-brand-gold" aria-hidden="true" />
            <div className="absolute top-4 left-4 bg-white border border-slate-200 px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-700">
              Our Team
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
